import { lazy, Suspense, useEffect, useState } from 'react';
import { getSmokeStateSnapshot, useGameStore } from '../state/useGameStore';
import { MainMenu } from '../ui/MainMenu';
import { PauseMenu } from '../ui/PauseMenu';
import { Hud } from '../ui/Hud';
import { LoadingScreen } from '../ui/LoadingScreen';
import { MapBriefing } from '../ui/MapBriefing';
import { PostMatchOverlay } from '../ui/PostMatchOverlay';
import { ErrorBoundary } from '../ui/ErrorBoundary';
import { requestPointerLockSafe } from '../game/pointerLock';
import { RespawnScreen } from '../ui/RespawnScreen';
import type { ArmorClass } from '../shared/game';

declare global {
  interface Window {
    tribes2SmokeState?: typeof getSmokeStateSnapshot;
  }
}

const GameCanvas = lazy(() => import('../game/GameCanvas').then((module) => ({ default: module.GameCanvas })));

function GameContent() {
  const phase = useGameStore((state) => state.gamePhase);
  const localPlayer = useGameStore((state) => state.localPlayer);
  const setPendingLoadout = useGameStore((state) => state.setPendingLoadout);
  const mapId = useGameStore((state) => state.mapId);
  // Phase 138 — briefing shows once per map-load transition.
  const [briefingDismissed, setBriefingDismissed] = useState(false);
  useEffect(() => {
    if (phase !== 'playing') setBriefingDismissed(false);
  }, [phase, mapId]);

  const isDead = (localPlayer.respawnTimer ?? 0) > 0;
  const respawnCountdown = localPlayer.respawnTimer ?? 0;
  const currentArmorClass: ArmorClass = localPlayer.armorClass ?? 'medium';

  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        const currentPhase = useGameStore.getState().gamePhase;
        if (currentPhase === 'playing') {
          useGameStore.getState().setGamePhase('paused');
          document.exitPointerLock?.();
        } else if (currentPhase === 'paused') {
          useGameStore.getState().setGamePhase('playing');
          requestPointerLockSafe(document.body);
        }
      }
    }
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  if (phase === 'menu') {
    return <MainMenu />;
  }

  return (
    <>
      <Suspense fallback={<LoadingScreen />}>
        <ErrorBoundary>
          <GameCanvas />
        </ErrorBoundary>
      </Suspense>
      <Hud />
      {phase === 'playing' && !briefingDismissed && (
        <MapBriefing mapId={mapId} onComplete={() => setBriefingDismissed(true)} />
      )}
      {phase === 'paused' && <PauseMenu />}
      {phase === 'postmatch' && <PostMatchOverlay />}
      {isDead && phase === 'playing' && (
        <RespawnScreen
          respawnCountdown={respawnCountdown}
          currentArmorClass={currentArmorClass}
          onConfirm={(armor, weaponId) => {
            setPendingLoadout(armor, [weaponId]);
          }}
        />
      )}
    </>
  );
}

export function App() {
  useEffect(() => {
    window.tribes2SmokeState = getSmokeStateSnapshot;
    return () => {
      if (window.tribes2SmokeState === getSmokeStateSnapshot) {
        delete window.tribes2SmokeState;
      }
    };
  }, []);

  return <GameContent />;
}
